import type { ChartInstance } from "./kline-helpers";
import type { DrawingInstance, DrawingPoint, DrawingType } from "@/lib/chart-drawings/types";
import { getDrawingEngine } from "./drawings-KLine/engines";
import { pointsEqual, safeRemoveOverlay } from "./drawings-KLine/overlay-utils";

export interface DrawingSyncState {
  pointsJson: string;
  settingsJson: string;
  visible: boolean;
}

export type DrawingSyncStateMap = Map<string, DrawingSyncState>;

function snapshotInstance(instance: DrawingInstance): DrawingSyncState {
  return {
    pointsJson: JSON.stringify(instance.settings?.points ?? []),
    // DR-02: snapshot the whole settings object so edits to text / levels /
    // values / angle / fill (not just style) are detected and pushed to the
    // overlay, rather than being silently dropped.
    settingsJson: JSON.stringify(instance.settings ?? {}),
    visible: instance.visible !== false,
  };
}

function onPointsChangeFactory(
  updateSettings: (id: string, update: { points: DrawingPoint[] }) => void
) {
  return (id: string, points: DrawingPoint[]) => {
    updateSettings(id, { points });
  };
}

/**
 * Diff-based sync: style-only → override; points changed → recreate; visibility → override.
 */
export function syncDrawings(
  chart: ChartInstance,
  instances: DrawingInstance[],
  stateMap: DrawingSyncStateMap,
  updateSettings: (id: string, update: Record<string, unknown>) => void
): void {
  const onPointsChange = onPointsChangeFactory(updateSettings);

  for (const instance of instances) {
    const engine = getDrawingEngine(instance.type);
    if (!engine) {
      console.warn(`Unsupported drawing type for sync: ${instance.type}`);
      continue;
    }

    const prev = stateMap.get(instance.id);
    const next = snapshotInstance(instance);

    if (!prev) {
      engine.load(chart, instance, onPointsChange);
      stateMap.set(instance.id, next);
      continue;
    }

    const pointsChanged = prev.pointsJson !== next.pointsJson;
    const settingsChanged = prev.settingsJson !== next.settingsJson;
    const visibilityChanged = prev.visible !== next.visible;

    if (pointsChanged) {
      engine.recreate(chart, instance, onPointsChange);
    } else if (settingsChanged || visibilityChanged) {
      // DR-02: any non-point settings change (style, text, levels, values,
      // angle, fill, …) or visibility flip is pushed via update. Skipped when
      // the snapshot is unchanged to avoid a per-frame perf regression.
      engine.update(chart, instance);
    }

    stateMap.set(instance.id, next);
  }
}

/**
 * Initial load for symbol / timeframe — creates overlays for all instances.
 */
export function loadDrawings(
  chart: ChartInstance,
  instances: DrawingInstance[],
  stateMap: DrawingSyncStateMap,
  updateSettings: (id: string, update: Record<string, unknown>) => void
): void {
  const onPointsChange = onPointsChangeFactory(updateSettings);

  for (const instance of instances) {
    const engine = getDrawingEngine(instance.type);
    if (!engine) continue;
    try {
      engine.load(chart, instance, onPointsChange);
      stateMap.set(instance.id, snapshotInstance(instance));
    } catch (error) {
      console.error(`Failed to load drawing ${instance.id}:`, error);
    }
  }
}

export function removeStaleOverlays(
  chart: ChartInstance,
  trackedIds: Set<string>,
  currentIds: Set<string>
): void {
  trackedIds.forEach((id) => {
    if (!currentIds.has(id)) {
      safeRemoveOverlay(chart, id);
      trackedIds.delete(id);
    }
  });
}

export { createDrawing } from "./drawings-KLine/engines";
export type { DrawingEngineContext } from "./drawings-KLine/types";

/** @deprecated use syncDrawings */
export const updateChartObjects = syncDrawings;

/** @deprecated use loadDrawings */
export const loadChartObjects = loadDrawings;
